import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Question, Sparkle, Quotes, BookOpen } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const VOICES = [
  { key: 'materia', label: 'Materia energia', color: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100' },
  { key: 'trasporto', label: 'Trasporto', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
  { key: 'oneri', label: 'Oneri di sistema', color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100' },
  { key: 'accise', label: 'Accise', color: 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100' },
  { key: 'iva', label: 'IVA', color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' },
];

export default function SpiegaVoceBar({ billContext }) {
  const [open, setOpen] = useState(false);
  const [activeVoice, setActiveVoice] = useState(null);
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noSources, setNoSources] = useState(false);

  const explain = async (voice) => {
    setActiveVoice(voice);
    setOpen(true);
    setAnswer('');
    setCitations([]);
    setNoSources(false);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/knowledge/explain-voice`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice: voice.key, bill_context: billContext || null }),
      });
      const d = await res.json();
      if (res.ok) {
        setAnswer(d.answer || '—');
        setCitations(d.citations || []);
        setNoSources(!!d.no_sources);
      } else {
        toast.error(d.detail || 'Errore');
        setOpen(false);
      }
    } catch (e) {
      toast.error('Errore di rete');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="border border-orange-100 bg-orange-50/30 rounded-lg p-3 mt-3" data-testid="spiega-voce-bar">
        <div className="text-xs text-slate-600 font-medium mb-2 flex items-center gap-1">
          <BookOpen size={14} weight="duotone" className="text-orange-500" />
          Hai dubbi su una voce della fattura? Chiedi all'esperto ARERA:
        </div>
        <div className="flex flex-wrap gap-2">
          {VOICES.map(v => (
            <button key={v.key} onClick={() => explain(v)}
                    data-testid={`spiega-voce-${v.key}`}
                    className={`text-xs px-3 py-1.5 border rounded-full ${v.color} transition flex items-center gap-1`}>
              <Question size={12} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={20} weight="duotone" className="text-orange-500" />
              {activeVoice?.label}
              <Badge variant="outline" className="text-[10px]">Knowledge ARERA</Badge>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center text-slate-500">
              <div className="flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce [animation-delay:0.1s]"></div>
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
              </div>
              <p className="text-sm mt-3">Consulto i Testi Integrati...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {noSources && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                  ⚠ Knowledge base vuota. Vai su <strong>Knowledge ARERA</strong> e premi "Carica Testi Integrati".
                </div>
              )}
              <div className="text-sm whitespace-pre-wrap leading-relaxed text-slate-800 bg-white border rounded-lg p-4"
                   data-testid="spiega-voce-answer">
                {answer}
              </div>
              {citations.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    <Quotes size={12} /> Fonti citate:
                  </div>
                  {citations.map(c => (
                    <div key={c.n} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-orange-600">[{c.n}]</span>
                        <span className="font-medium">{c.label}</span>
                        <span className="text-slate-500">— pag. {c.page}</span>
                      </div>
                      {c.preview && (
                        <div className="text-[10px] text-slate-500 mt-1 italic line-clamp-2">"{c.preview}..."</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
